import { useMemo, useState } from 'react';
import { MapPin, Plus, Trash2, X } from 'lucide-react';
import type { Court } from '@airmail/engine';
import { useTurnier } from '@/store/TournamentProvider';
import { Button, Empty, Field, Input, SectionHead, cx } from '@/ui';
import { confirmDialog } from '@/ui/dialogs';
import { useT } from '@/i18n';

/**
 * Felder — nach Spielorten sortiert (docs/notes/spielorte.md).
 *
 * Ein Spielort ist `Court.group`, der Feldname `Court.name`. Angelegt wird von
 * oben nach unten: erst der Ort mit der Anzahl seiner Felder („Piazza“ mit 5),
 * die Felder heißen dann „Piazza 1“ … „Piazza 5“. Ohne Ortsnamen bleibt alles
 * wie gehabt: „Feld 1“ … „Feld n“ ohne Ort.
 *
 * Ein Feld löschen geht nicht: die Engine kennt nur `court.add` und
 * `court.update` (packages/engine/src/engine/reduce.ts) — ein nicht mehr
 * benutztes Feld wird auf „Aktiv“ ausgeschaltet und bekommt keine Spiele mehr.
 */
export function CourtsPage() {
  const t = useT();
  const { tournament, dispatch } = useTurnier();
  const [venueName, setVenueName] = useState('');
  const [venueCount, setVenueCount] = useState('4');
  const courts = tournament.courts;

  // Felder, auf denen schon ein Spiel liegt: die Engine lässt sie nicht mehr löschen,
  // sonst zeigte das Protokoll auf ein Feld, das es nicht mehr gibt
  const played = useMemo(() => new Set(tournament.competitions.flatMap((c) => c.phases.flatMap((p) => p.matches.map((m) => m.courtId).filter((id): id is string => !!id)))), [tournament]);

  const venues = useMemo(() => [...new Set(courts.map((c) => c.group).filter((g): g is string => !!g))].sort((a, b) => a.localeCompare(b)), [courts]);
  // Orte alphabetisch, Felder ohne Ort zuletzt
  const groups = useMemo(() => {
    const list: { venue: string; courts: Court[] }[] = venues.map((v) => ({ venue: v, courts: courts.filter((c) => c.group === v) }));
    const loose = courts.filter((c) => !c.group);
    if (loose.length > 0) list.push({ venue: '', courts: loose });
    return list;
  }, [courts, venues]);

  /** Ganzer Spielort auf einen Schlag: n Felder „Piazza 1“ … „Piazza n“ */
  const addVenue = () => {
    const n = Number(venueCount);
    if (!n || n < 1) return;
    const place = venueName.trim();
    const start = place ? courts.filter((c) => c.group === place).length : courts.length;
    for (let i = 0; i < n; i++) {
      const name = place ? `${place} ${start + i + 1}` : `Feld ${start + i + 1}`;
      dispatch({ type: 'court.add', name, ...(place ? { group: place } : {}) }, i < n - 1 ? { silent: true } : undefined);
    }
    setVenueName('');
  };

  /** Ein weiteres Feld an einen Ort hängen — fortlaufend nummeriert */
  const addCourtTo = (venue: string) => {
    const n = venue ? courts.filter((c) => c.group === venue).length : courts.length;
    dispatch({ type: 'court.add', name: venue ? `${venue} ${n + 1}` : `Feld ${n + 1}`, ...(venue ? { group: venue } : {}) });
  };

  /** Versehentlich angelegtes Feld wieder weg — nur solange kein Spiel darauf liegt */
  const removeCourt = async (court: Court) => {
    if (!(await confirmDialog(t('Feld „{name}“ löschen?', { name: court.name }), { ok: t('Löschen'), danger: true }))) return;
    dispatch({ type: 'court.remove', courtId: court.id });
  };

  /** Umbenennen zieht den Namensteil der Felder mit: „Piazza 1“ → „Sportplatz 1“ */
  const renameVenue = (from: string, to: string) => {
    const next = to.trim();
    if (!next || next === from) return;
    const list = courts.filter((c) => c.group === from);
    list.forEach((c, i) => {
      const name = c.name.startsWith(from) ? next + c.name.slice(from.length) : c.name;
      dispatch({ type: 'court.update', courtId: c.id, patch: { group: next, name } }, i < list.length - 1 ? { silent: true } : undefined);
    });
  };

  /** Ort auflösen: die Felder bleiben, sie haben danach nur keinen Ort mehr */
  const removeVenue = async (venue: string) => {
    if (!(await confirmDialog(t('Spielort „{name}“ entfernen? Die Felder bleiben erhalten und haben dann keinen Ort mehr.', { name: venue }), { ok: t('Entfernen'), danger: true }))) return;
    const list = courts.filter((c) => c.group === venue);
    list.forEach((c, i) => dispatch({ type: 'court.update', courtId: c.id, patch: { group: undefined } }, i < list.length - 1 ? { silent: true } : undefined));
  };

  return (
    <div className="max-w-3xl">
      <SectionHead title={t('Felder')} meta={t('{a} aktiv von {n}', { a: courts.filter((c) => c.active).length, n: courts.length })} />
      <p className="text-sm text-muted mb-4">
        {t('Inaktive Felder bekommen keine Spiele (Board kaputt, Regen).')} <b>{t('Platz')}</b>{' '}
        {t(
          '(z. B. „Piazza“, „Sportplatz“, „Halle“) fasst Felder eines Spielorts zusammen: In der Vorrunde bekommt jede Gruppe ihren Platz, unter „Spiele“ filtert die Turnierleitung vor Ort auf ihren Platz.',
        )}{' '}
        <b>{t('Priorität')}</b>: {t('höher = wird zuerst belegt — Finale auf den Center Court.')}
      </p>

      <div className="border border-line bg-panel px-3 py-3 mb-6">
        <div className="label mb-2">{t('Spielort hinzufügen')}</div>
        <div className="flex flex-wrap items-end gap-3">
          <Field label={t('Name des Spielorts')} className="w-44">
            <Input className="py-1.5 text-sm" placeholder={t('z. B. Piazza')} value={venueName} onChange={(e) => setVenueName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addVenue()} />
          </Field>
          <Field label={t('Anzahl Felder')} className="w-24">
            <Input className="py-1.5 text-sm" type="number" min={1} max={32} value={venueCount} onChange={(e) => setVenueCount(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addVenue()} />
          </Field>
          <Button variant="primary" className="mb-[1px]" onClick={addVenue} disabled={!Number(venueCount)}>
            <Plus size={14} /> {t('Spielort anlegen')}
          </Button>
        </div>
        <p className="text-xs text-dim mt-2 mb-0">
          {t('Die Felder heißen dann „Piazza 1“, „Piazza 2“ … Ohne Ortsnamen entstehen „Feld 1“ … „Feld n“ ganz ohne Ort — für ein Turnier an einem einzigen Ort reicht das.')}
        </p>
      </div>

      <datalist id="airmail-court-groups">
        {venues.map((g) => (
          <option key={g} value={g} />
        ))}
      </datalist>

      {courts.length === 0 ? (
        <Empty title={t('Noch keine Felder.')} />
      ) : (
        groups.map((g) => (
          <section key={g.venue || '—'} className="mb-6">
            <div className="flex items-center gap-2 flex-wrap border-b border-line pb-1.5 mb-2">
              <MapPin size={13} className="text-dim shrink-0" />
              <b className="text-ink">{g.venue || t('Ohne Spielort')}</b>
              <span className="font-mono text-[11px] text-dim">{t('{n} Felder · {a} aktiv', { n: g.courts.length, a: g.courts.filter((c) => c.active).length })}</span>
              <div className="ml-auto flex items-center gap-2 flex-wrap justify-end">
                {g.venue && (
                  <div className="w-36 shrink-0">
                    {/* Schlüssel = Ortsname: nach dem Umbenennen zeigt das Feld den neuen Namen */}
                    <Input
                      key={g.venue}
                      className="py-0.5 px-2 text-xs"
                      defaultValue={g.venue}
                      aria-label={t('Spielort umbenennen')}
                      title={t('Spielort umbenennen')}
                      onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
                      onBlur={(e) => renameVenue(g.venue, e.target.value)}
                    />
                  </div>
                )}
                <Button size="sm" onClick={() => addCourtTo(g.venue)}>
                  <Plus size={12} /> {t('Feld hinzufügen')}
                </Button>
                {g.venue && (
                  <Button size="sm" variant="ghost" title={t('Spielort entfernen')} onClick={() => removeVenue(g.venue)}>
                    <X size={12} /> {t('Spielort entfernen')}
                  </Button>
                )}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm min-w-[520px]">
                <thead>
                  <tr className="border-b border-line">
                    <th className="label font-normal text-left px-2 py-2">{t('Name')}</th>
                    <th className="label font-normal text-right px-2 py-2">{t('Priorität')}</th>
                    <th className="label font-normal text-center px-2 py-2">{t('Aktiv')}</th>
                    <th className="label font-normal text-left px-2 py-2">{t('Belegung')}</th>
                    <th className="label font-normal text-left px-2 py-2">{t('Ort wechseln')}</th>
                    <th className="label font-normal text-center px-2 py-2 w-10" />
                  </tr>
                </thead>
                <tbody>
                  {g.courts.map((c) => (
                    <tr key={c.id} className={cx('border-b border-line2', !c.active && 'opacity-50')}>
                      <td className="px-2 py-1.5">
                        {/* Schlüssel = Name: benennt das Umbenennen des Orts die Felder mit um, steht hier der neue Name */}
                        <Input
                          key={c.name}
                          className="py-1 font-mono text-sm"
                          defaultValue={c.name}
                          onBlur={(e) => e.target.value.trim() && e.target.value !== c.name && dispatch({ type: 'court.update', courtId: c.id, patch: { name: e.target.value.trim() } })}
                        />
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        <Input
                          className="py-1 w-20 text-right font-mono text-sm"
                          type="number"
                          defaultValue={c.priority}
                          onBlur={(e) => Number(e.target.value) !== c.priority && dispatch({ type: 'court.update', courtId: c.id, patch: { priority: Number(e.target.value) || 0 } })}
                        />
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        <input type="checkbox" checked={c.active} onChange={(e) => dispatch({ type: 'court.update', courtId: c.id, patch: { active: e.target.checked } })} />
                      </td>
                      <td className="px-2 py-1.5 font-mono text-[11px]">{c.currentMatchId ? <span className="text-live">{t('Spiel läuft')}</span> : <span className="text-dim">{t('frei')}</span>}</td>
                      <td className="px-2 py-1.5">
                        {/* Einzelnes Feld umziehen: Liste aller Orte zum Auswählen, leer = ohne Ort */}
                        <Input
                          key={c.group ?? ''}
                          className="py-1 text-sm w-32"
                          defaultValue={c.group ?? ''}
                          placeholder={t('z. B. Piazza')}
                          list="airmail-court-groups"
                          title={t('leer lassen = ohne Spielort')}
                          aria-label={t('Spielort von {c}', { c: c.name })}
                          onBlur={(e) => (e.target.value.trim() || undefined) !== c.group && dispatch({ type: 'court.update', courtId: c.id, patch: { group: e.target.value.trim() || undefined } })}
                        />
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="px-1.5"
                          disabled={played.has(c.id) || !!c.currentMatchId}
                          title={played.has(c.id) || c.currentMatchId ? t('Auf diesem Feld wird schon gespielt — es lässt sich nur noch abschalten.') : t('Feld löschen')}
                          aria-label={t('Feld {c} löschen', { c: c.name })}
                          onClick={() => removeCourt(c)}
                        >
                          <Trash2 size={13} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ))
      )}
    </div>
  );
}
